import { useState, useEffect } from "react";

import { initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from "firebase/auth";
import {
  addDoc,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getCountFromServer,
  getDocs,
  getFirestore,
  onSnapshot,
  query,
  runTransaction,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { getStorage } from "firebase/storage";

//configuration
export const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
};
const app = initializeApp(firebaseConfig);

//Authorization
export const auth = getAuth(app);
export const googleAuth = getAuth(app);
export default app;

export const logOut = async () => {
  try {
    await signOut(auth).then(() => {
      console.log("Signed Out");
    });
  } catch (error) {
    console.log("sign out error: ", error);
  }
};

export const logIn = (email, password) => {
  return signInWithEmailAndPassword(auth, email, password);
};

const googleProvider = new GoogleAuthProvider();

export const logInWithGoogle = () => signInWithPopup(auth, googleProvider);

export const useAuth = () => {
  const [currentUser, setCurrentUser] = useState();
  useEffect(() => {
    console.log("useAuth fired");
    const unsubscribe = onAuthStateChanged(auth, (user) =>
      setCurrentUser(user)
    );
    return unsubscribe();
  }, []);
  return currentUser;
};

//Firestore
export const createNamedDocument = async (documentReference, payload) => {
  await setDoc(documentReference, payload);
};
export const createUnNamedDocument = async (collectionReference, payload) => {
  await addDoc(collectionReference, payload);
};
export const updateDocument = async (documentReference, payload) => {
  await updateDoc(documentReference, payload);
};
export const deleteDocument = async (documentReference) => {
  await deleteDoc(documentReference);
};

//get the database
export const db = getFirestore(app);

//sync
export const useCollectionListener = (collectionName) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const ref = collection(db, collectionName);

    const unsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        setLoading(false);
        setData(snapshot.docs.map((doc) => doc.data()));
      },
      (err) => {
        setLoading(false);
        setError(err);
      }
    );

    return unsubscribe;
  }, [collectionName]);

  return { data, loading, error };
};

export const useSyncedCollection = (collection1, enabled = true) => {
  const [syncedCollection, setSyncedCollection] = useState([]);

  useEffect(() => {
    if (!enabled || !collection1) {
      setSyncedCollection([]);
      return undefined;
    }

    const q = query(collection(db, collection1));

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docsArray = [];
        snapshot.forEach((docSnap) => {
          // Doc id must win over any `id` field stored in the document data
          docsArray.push({ ...docSnap.data(), id: docSnap.id });
        });
        setSyncedCollection(docsArray);
      },
      (error) => {
        console.error(`Firestore listener error (${collection1}):`, error);
      },
    );
    return () => unsubscribe();
  }, [collection1, enabled]);
  return syncedCollection;
};

export const useSyncedNestedCollection = (collection1, id, collection2) => {
  const [nestedCollection, setNestedCollection] = useState([]);

  useEffect(() => {
    if (!id) {
      setNestedCollection([]);
      return undefined;
    }

    const q = query(collection(db, collection1, id, collection2));
    //console.log("use syncedNestedCollection Fired");
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docsArray = [];
      snapshot.forEach((docSnap) => {
        docsArray.push({ ...docSnap.data(), id: docSnap.id });
      });
      setNestedCollection(docsArray);
    });
    return () => unsubscribe();
  }, [collection1, id, collection2]);
  return nestedCollection;
};

export const useSyncedDocument = (collection, id) => {
  const [document, setDocument] = useState({});
  useEffect(() => {
    if (!id) {
      return undefined;
    }

    const subscribeToDocument = onSnapshot(
      doc(db, collection, id),
      (docSnap) => {
        setDocument({ ...docSnap.data(), id: docSnap.id });
      },
      (error) => {
        console.error(`Firestore listener error (${collection}/${id}):`, error);
      },
    );
    return () => subscribeToDocument();
  }, [collection, id]);
  return document;
};

export const useSyncedNestedDocument = (collection1, id1, collection2, id2) => {
  const [document, setDocument] = useState({});

  useEffect(() => {
    const subscribeToDocument = onSnapshot(
      doc(db, collection1, id1, collection2, id2),
      (doc) => {
        setDocument({ ...doc.data(), id: doc.id });
      }
    );
    return () => subscribeToDocument();
  }, [collection1, id1, collection2, id2]);
  return document;
};

export const useSyncedLabels = () => {
  const [labels, setLabels] = useState([]);

  useEffect(() => {
    const q = query(collection(db, "calLabel"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const labelArray = [];
      snapshot.forEach((doc) => {
        labelArray.push({
          ...doc.data(),
          id: doc.id,
          labelDate: doc.data().labelDate.toDate(),
        });
      });
      setLabels(labelArray);
    });
    return () => unsubscribe();
  }, []);
  return labels;
};

export const useSyncedEvents = () => {
  const [documents, setDocuments] = useState([]);

  useEffect(() => {
    const q = query(collection(db, "events"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const eventArray = [];
      snapshot.forEach((doc) => {
        doc.data().dateScheduled
          ? eventArray.push({
              ...doc.data(),
              id: doc.id,
              start: doc.data().start.toDate(),
              end: doc.data().end.toDate(),
              dateCreated: doc.data().dateCreated.toDate(),
              dateModified: doc.data().dateModified.toDate(),
              dateScheduled: doc.data().dateScheduled.toDate(),
            })
          : eventArray.push({
              ...doc.data(),
              id: doc.id,
              start: null,
              end: null,
              dateCreated: doc.data().dateCreated.toDate(),
              dateModified: doc.data().dateModified.toDate(),
              dateScheduled: null,
            });
      });
      setDocuments(eventArray);
    });
    return () => unsubscribe();
  }, []);
  return documents;
};

export const getCollectionSize = async (col) => {
  const collectionRef = collection(db, col);
  const snapshot = await getCountFromServer(collectionRef);
  return snapshot.data().count;
};

//edit part
const projectStorage = getStorage(app);
export { projectStorage };
const firestore = getFirestore(app);
export { firestore };

//change a field in all documents in a given collection
export const changeFieldInAllDocuments = async (col, field) => {
  let count = 0;
  const q = query(collection(db, `${col}`));
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach((doc) => {
    count += 1;
    updateDoc(doc.ref, {
      [field]: [],
    })
      .then(() => {
        console.log(`${field} updated successfully`);
        console.log(`number of docs updated: `, count);
      })
      .catch((error) => {
        count += 1;
        console.log(`number of docs failed: `, count);
        console.error("Error updating the docs", error);
      });
  });
};

export const customerContactUpdate = async () => {
  const q = query(collection(db, "customers"));
  const querySnapshot = await getDocs(q);

  querySnapshot.forEach(async (customer) => {
    const customerRef = doc(db, "customers", customer.id);

    try {
      await runTransaction(db, async (transaction) => {
        const custDoc = await transaction.get(customerRef);
        if (!custDoc.exists()) {
          throw new Error("Customer dose not exist");
        }
        const contact = {};
        //name
        const newNameContact = custDoc.data().billingOtherName;
        //prove the value is not null or undefined
        if (typeof newNameContact === "string") {
          //prove the value is not empty
          if (newNameContact === "") {
            console.log("newNameContact is empty");
          } else {
            contact.contactName = newNameContact;
          }
        } else {
          console.log("newNameContact is not a string");
        }

        //phone
        const newPhoneContact = custDoc.data().billingOtherPhone;
        //prove the value is not null or undefined
        if (typeof newPhoneContact === "string") {
          //prove the value is not empty
          if (newPhoneContact === "") {
            console.log("newPhoneContact is empty");
          } else {
            contact.contactPhone = newPhoneContact;
          }
        } else {
          console.log("newPhoneContact is not a string");
        }

        //phone
        const newEmailContact = custDoc.data().billingOtherEmail;
        //prove the value is not null or undefined
        if (typeof newEmailContact === "string") {
          //prove the value is not empty
          if (newEmailContact === "") {
            console.log("newEmailContact is empty");
          } else {
            contact.contactEmail = newEmailContact;
          }
        } else {
          console.log("newEmailContact is not a string");
        }

        //check to see if the contact object is empty
        const contactIsEmpty = Object.keys(contact).length === 0;

        if (contactIsEmpty) {
          console.log(
            `customer ${
              custDoc.data().lastname
            } has no other billing information`
          );
          return Promise.reject("The contact billing object is empty");
        } else {
          //the contact object has a phone number or name, add the empty notes
          contact.contactNotes = "";

          //if the contact has a phoneName but has no phone create a empty phone
          if (contact.contactName) {
            if (!contact.contactPhone) {
              contact.contactPhone = "";
            }
          }

          //if the contact has a phone but has no phoneName create a empty phoneName
          if (contact.contactPhone) {
            if (!contact.contactName) {
              contact.contactName = "";
            }
          }

          //if the contact has a phone or name but has no email create a empty email
          if (contact.contactPhone || contact.contactName) {
            if (!contact.contactEmail) {
              contact.contactEmail = "";
            }
          }

          const hasDoneEmail = "contactEmail" in contact;
          const hasDoneName = "contactName" in contact;
          const hasDoneNotes = "contactNotes" in contact;
          const hasDonePhone = "contactPhone" in contact;

          //verify we have everything we just built
          if (hasDoneEmail && hasDoneName && hasDoneNotes && hasDonePhone) {
            console.log(
              `customer ${
                custDoc.data().lastname
              } billingContact information updated!`
            );
            //update firestore
            transaction.update(customerRef, {
              commercialContacts: arrayUnion(contact),
            });
            return contact;
          } else {
            console.log("something is empty");
          }
        }
      });
    } catch (e) {
      console.error(e);
    }
  });
};

export const checkForMaint = async (docId) => {
  const q = query(collection(db, "customers", docId, "Maintenance"));
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach((docu) => {
    console.log("maintenance found: ", docu.id);
    //delete all maintnenance
    deleteDoc(doc(db, "customers", docId, "Maintenance", docu.id)).then(() => {
      console.log("deleted");
    });
  });
};

export const deleteMaintenance = async () => {
  const q = query(collection(db, "customers"));
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach((doc) => {
    checkForMaint(doc.id);
  });
};

export const deleteQueryBatch = async (db, q, resolve) => {
  const snapshot = await getDocs(q);

  const batchSize = await getCountFromServer(q);
  console.log("Batch Size: ", batchSize.data().count);

  if (batchSize.data().count === 0) {
    resolve();
    return;
  }

  //delete all documents in a batch
  const batch = writeBatch(db);
  snapshot.forEach((doc) => {
    batch.delete(doc.ref);
  });
  await batch.commit();

  //recurse on the next preccess tick to avoid blowing the stack
  // process.nextTick(() => {
  //     deleteQueryBatch(db, q, resolve)
  // })
};

// export const deleteCollection = async (collectionPath) => {
//   const collectionRef = collection(db, collectionPath)
//   const q = query(collectionRef, orderBy(`__name__`), limit(500))

//   return new Promise((resolve, reject) => {
//       deleteQueryBatch(db, q, resolve).catch(reject)
//   })
// }
